/**
 * Number formatting for mechanics results: significant figures and scientific notation.
 *
 * Physics answers carry a chosen number of significant figures and run from 10⁻¹⁹ J (an
 * electron volt) to 10⁹ W. These helpers write a number the way a worked solution would
 * — "12.35", "1.602 × 10⁻¹⁹" — using the reader's decimal mark.
 */

export type NumberFormatter = (
  value: number,
  options?: Intl.NumberFormatOptions
) => string;

const SUPERSCRIPT_DIGITS = "⁰¹²³⁴⁵⁶⁷⁸⁹";
const PLAIN_LOW = 1e-3;
const PLAIN_HIGH = 1e6;
const MAX_SIG = 17;

/** An integer exponent in Unicode superscripts: −8 → "⁻⁸". */
export function superscriptInt(value: number): string {
  let out = value < 0 ? "⁻" : "";
  for (const digit of String(Math.abs(value))) {
    out += SUPERSCRIPT_DIGITS[Number(digit)];
  }
  return out;
}

/** `1.602 × 10⁻¹⁹`, with the mantissa in the reader's decimal mark (no padded zeros). */
export function scientific(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  const [mantissa, exponent] = value.toExponential(sig - 1).split("e");
  const shown = fmt(Number(mantissa), {
    maximumFractionDigits: sig - 1,
    useGrouping: false,
  });
  return `${shown} × 10${superscriptInt(Number(exponent))}`;
}

/**
 * Round to `sig` significant figures: plain for everyday magnitudes (0.001 up to
 * 999,999), scientific notation beyond. Zero is "0"; trailing zeros are not padded (12 stays 12, not 12.00).
 */
export function formatSig(
  fmt: NumberFormatter,
  value: number,
  sig: number
): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  if (value === 0) {
    return fmt(0);
  }
  const clamped = Math.min(Math.max(Math.round(sig), 1), MAX_SIG);
  const magnitude = Math.abs(value);
  // Rounding can carry a value over a boundary (999,999.7 → 1,000,000), so test the
  // rounded magnitude.
  const rounded = Number(magnitude.toPrecision(clamped));
  if (rounded < PLAIN_LOW || rounded >= PLAIN_HIGH) {
    return scientific(fmt, value, clamped);
  }
  return fmt(value, {
    maximumSignificantDigits: clamped,
    useGrouping: false,
  });
}

/** A full-precision echo of an input or intermediate value without float noise. */
export function formatPlain(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const magnitude = Math.abs(value);
  if (magnitude !== 0 && (magnitude < PLAIN_LOW || magnitude >= 1e9)) {
    return scientific(fmt, value, 5);
  }
  return fmt(value, { maximumSignificantDigits: 8, useGrouping: false });
}

/** Choices for the "round to" menu. */
export const SIG_FIG_CHOICES = ["3", "4", "5", "6"] as const;
export type SigFigChoice = (typeof SIG_FIG_CHOICES)[number];
export const DEFAULT_SIG_FIGS: SigFigChoice = "4";

/** A number and its unit: "12 m/s", but "30°" and "75%" — those signs take no space. */
export function withUnit(text: string, symbol: string): string {
  if (symbol === "") {
    return text;
  }
  return symbol === "°" || symbol === "%"
    ? `${text}${symbol}`
    : `${text} ${symbol}`;
}
